import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, TextareaAutosize } from "@mui/material";

import Title from "../../../common/Title";
import InputFieldAutoComplete from "../../../common/InputFieldAutoComplete";
import InputFieldBox from "../../../common/InputFieldBox";

import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import {
  __imsiRegister,
  __imsiUnregister,
  __getOrganizations,
} from "../../../../api/sim";
import { OPERATIONS } from "../../../../constants/value";

const IMSIForm = ({ operation }) => {
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [organizations, setOrganizations] = useState([]);

  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [provider, setProvider] = useState("");
  const [isLoaner, setIsLoaner] = useState(false);
  const [isVoice, setIsVoice] = useState(false);

  const { setAlertMessage } = useAlert();
  const initialData = { lines: [] };
  const [registerResponse, registerCall, registerLoading] = useAxios({
    ...__imsiRegister,
  });
  const [unregisterResponse, unregisterCall, unregisterLoading] = useAxios({
    ...__imsiUnregister,
  });
  const [orgResponse, orgCall, orgLoading] = useAxios({
    ...__getOrganizations,
  });
  const nav = useNavigate();

  const lineValidationSchema = yup.object({
    imsi: yup.string("imsi").required(),
  });

  const apiCall = (requestData) => {
    requestData.provider = provider || null;
    requestData.is_loaner = isLoaner;
    requestData.is_voice = isVoice;
    
    if (operation === OPERATIONS.imsi_create) {
      requestData.organization_uuid = currentOrganization;
    }

    const call =
      operation === OPERATIONS.imsi_create ? registerCall : unregisterCall;

    call({
      data: requestData,
    });
  };

  useEffect(() => {
    const response =
      operation === OPERATIONS.imsi_create
        ? registerResponse
        : unregisterResponse;

    if (!response) return;

    if (response.status) {
      setAlertMessage({
        type: "success",
        message: `IMSI ${
          operation === OPERATIONS.imsi_create ? "create" : "delete"
        } queue started`,
      });
      setLinesData([]);
      nav(`/sparq/imsi/${operation}`);
    }
    formik.setSubmitting(false);
  }, [registerResponse, unregisterResponse]);

  useEffect(() => {
    orgCall({ params: { is_voice: isVoice } });
  }, [isVoice]);

  useEffect(() => {
    if (orgResponse && orgResponse.status) {
      setOrganizations(orgResponse.data);
    }
  }, [orgResponse]);

  const validateFunction = (data) => {
    const errors = {};
    const formatedLines = [];

    if (!data) {
      return errors;
    }

    const iccids = [];

    const lines = data.split("\n");
    lines.some((line, idx) => {
      if (!line.trim()) {
        return false; // Skip empty lines
      }

      try {
        const line_data = line.split(",");
        // Accept 1 field (IMSI only)
        if (line_data.length !== 1) {
          errors.lines = `Invalid format in line ${idx + 1}: Expected 1 field, got ${line_data.length}`;
          return true;
        }
        const obtainedData = {
          imsi: line_data[0].trim(),
        };

        lineValidationSchema.validateSync(obtainedData);

        if (isNaN(obtainedData.imsi)) {
          throw new Error("Invalid Number");
        }
        formatedLines.push(obtainedData);

        iccids.push(obtainedData.imsi);

        if (iccids.indexOf(obtainedData.imsi) !== idx) {
          errors.lines = `Duplicate imsi value in line ${idx + 1} `;
          return true;
        }
      } catch (err) {
        console.log(err);
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }

      return false;
    });

    if (!errors.lines) {
      setLinesData(formatedLines);
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: { ...initialData },
    onSubmit: (values) => {
      const requestData = Object.keys(values).reduce((obj, key) => {
        obj[key] = values[key] || null;
        return obj;
      }, {});

      requestData.lines = [...linesData];

      apiCall(requestData);
    },
    validateOnChange: true,
  });

  const handleParse = (inputFile) => {
    const reader = new FileReader();

    reader.onload = async ({ target }) => {
      setRawData(target.result);
      formik.setErrors(validateFunction(target.result));
    };
    reader.readAsText(inputFile);
  };
  const handleFileChange = (e) => {
    const allowedExtensions = ["csv"];

    // Check if user has entered the file
    if (e.target.files.length) {
      const inputFile = e.target.files[0];

      const fileExtension = inputFile?.type.split("/")[1];
      if (!allowedExtensions.includes(fileExtension)) {
        formik.setErrors({
          ...formik.errors.lines,
          upload: "Please input a csv file",
        });
        return;
      }

      handleParse(inputFile);
    }
  };

  const isLoading = () => {
    return (
      registerLoading || unregisterLoading || orgLoading || formik.isSubmitting
    );
  };

  if (isLoading()) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <>
      <Box
        sx={{ width: "100%" }}
        display="flex"
        alignItems="center"
        flexDirection="column"
      >
        <Title
          variant="h5"
          content={`IMSI ${operation === "imsi_create" ? "Create" : "Delete"}`}
          sx={{ mb: 3 }}
          gutterBottom
        />
        <Grid
          sx={{ flexGrow: 1 }}
          container
          spacing={2}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12}>
            <FormControl
              error={Boolean(formik.errors.upload) ? true : false}
              sx={{ width: "100%" }}
            >
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                sx={{ marginRight: "1rem" }}
              >
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
              {Boolean(formik.errors.upload) ? (
                <FormHelperText id="upload-error-text">
                  {formik.errors.upload}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>
          <Grid item xs={12} minWidth={350}>
            <FormControl sx={{ width: "100%" }}>
              <InputFieldBox
                isCheckbox={true}
                value={isVoice}
                onChange={(event) => {
                  setIsVoice(event.target.checked);
                  setCurrentOrganization(null);
                }}
                label="Is Voice"
              />
            </FormControl>
          </Grid>
          {operation === OPERATIONS.imsi_create && (
            <>
              <Grid item xs={12} minWidth={350}>
                <FormControl
                  error={
                    formik.touched.user_id && Boolean(formik.errors.user_id)
                      ? true
                      : false
                  }
                  sx={{ width: "100%" }}
                >
                  <InputFieldAutoComplete
                    isSelect
                    options={[
                      ...organizations.reduce((acc, org) => {
                        const val = {
                          value: org.uuid,
                          label: org.name,
                        };
                        if (org.is_active) acc.push(val);
                        return acc;
                      }, []),
                    ]}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    name="organization"
                    label="Organization"
                    id="organization"
                    onChange={(e, value) =>
                      setCurrentOrganization(value?.value || null)
                    }
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} minWidth={350}>
                <FormControl sx={{ width: "100%" }}>
                  <InputFieldBox
                    type="text"
                    id="provider"
                    name="provider"
                    label="Provider"
                    value={provider}
                    onChange={(e) => setProvider(e.target.value)}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} minWidth={350}>
                <FormControl sx={{ width: "100%" }}>
                  <InputFieldBox
                    isCheckbox={true}
                    value={isLoaner}
                    onChange={(event) => setIsLoaner(event.target.checked)}
                    label="Is Loaner"
                  />
                </FormControl>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <InputLabel htmlFor="lines">
              {"Add in the format"}
              <br></br>
              {"IMSI"}
              <br></br>
            </InputLabel>
            <FormControl
              error={Boolean(formik.errors.lines) ? true : false}
              sx={{ width: "100%" }}
            >
              <TextareaAutosize
                multiline
                rows={6}
                aria-label="minimum height"
                placeholder={`Add in the format\nIMSI`}
                style={{ minWidth: 550 }}
                name="lines"
                minRows={5}
                id="lines"
                value={rawData}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const data = e.target.value;
                  setRawData(data);
                  formik.setErrors(validateFunction(data));
                }}
              />
              {Boolean(formik.errors.lines) ? (
                <FormHelperText id="lines-error-text">
                  {formik.errors.lines}
                </FormHelperText>
              ) : (
                ""
              )}
            </FormControl>
          </Grid>

          <Grid
            container
            sx={{ mt: 3 }}
            direction="row"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingButton
                style={{ marginRight: "5px" }}
                disabled={
                  formik.errors.lines ||
                  !linesData.length ||
                  (operation === OPERATIONS.imsi_create && !currentOrganization)
                }
                onClick={() =>
                  !isLoading() && !formik.isSubmitting && formik.submitForm()
                }
                size="large"
                loading={isLoading()}
                variant="contained"
              >
                {`IMSI ${
                  operation === OPERATIONS.imsi_create ? "create" : "delete"
                }`}
              </LoadingButton>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default IMSIForm;
